import React, { Component } from 'react';
import { defineMessages } from 'react-intl';
import PropTypes from 'prop-types';

import DataCacheService from '@/shared/services/DataCacheService';
import AuthService from '@/shared/services/AuthService';
import { guid1 } from '@/shared/utils/guid';
import intlShape from '@/shared/utils/intlShape';
import AutoCompleteWithoutInjectIntl from './select/AutoCompleteWithoutInjectIntl';

const messages = defineMessages({
  SearchEmployee: 'Search employee',
  Unassigned: 'Unassigned',
  AssignedToMe: 'Assigned to me',
  SystemUser: 'System user',
});

const mapUser = user =>
  !user
    ? null
    : {
        key: user.id || user.key,
        label: user.name || user.label,
        employeeId: user.employeeId,
        isActive: user.isActive,
        hidden: user.hidden,
      };

export const getUserIdFromValue = value => {
  if (!value) return null;
  if (typeof value === 'string') return value; // Value = UserId
  if (typeof value === 'object') {
    if (value.id) return value.id;
    if (value.key) return value.key;
  }
  return null;
};

export const userShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  hidden: PropTypes.bool,
});
export const getUserClassName = ({ isActive = true }) => {
  if (isActive) return undefined;
  return 'removed-user';
};

// this component exists because we cannot use the intl injection of the normal component in overlaytriggers. see https://github.com/react-bootstrap/react-bootstrap/pull/5778
class UserSearchWithoutInjectIntl extends Component {
  state = {
    selectedUser: null,
    internalUsers: [],
  };

  constructor(props) {
    super(props);
    const users = this.initUsers();
    const userId = getUserIdFromValue(props.value);
    const selectedUser = userId ? users.find(x => x.key === userId) : null;

    this.state = {
      ...this.state,
      internalUsers: users,
      selectedUser,
    };
    if (!selectedUser && userId) {
      props.onChange(null);
    }
  }

  initUsers = () => {
    const {
      users,
      withUnassigned,
      withCurrentUser,
      intl,
      unassignedId,
      withSystemUser,
    } = this.props;
    let newUsers = users || [];

    // retrieve users if none were passed by parent component
    if (!newUsers.length) {
      newUsers = DataCacheService.getUsers();
    }

    if (withSystemUser) {
      newUsers.unshift({
        id: guid1,
        name: intl.formatMessage(messages.SystemUser),
      });
    }
    if (withCurrentUser) {
      newUsers.unshift({
        id: AuthService.getUserId(),
        name: intl.formatMessage(messages.AssignedToMe),
      });
    }
    if (withUnassigned) {
      newUsers.unshift({
        id: unassignedId || null,
        name: intl.formatMessage(messages.Unassigned),
      });
    }
    return newUsers.map(mapUser);
  };

  onUserChanged = user => {
    if (user === undefined) return;
    const { onChange } = this.props;
    this.setState({ selectedUser: user });

    if (user === null) {
      onChange(null);
    } else {
      onChange({ id: user.key, name: user.label, employeeId: user.employeeId });
    }
  };

  componentDidUpdate(oldProps) {
    const { value, users, onChange } = this.props;
    const usersChanged = JSON.stringify(oldProps.users) !== JSON.stringify(users);
    const valueChanged = JSON.stringify(oldProps.value) !== JSON.stringify(value);
    if (!usersChanged && !valueChanged) return;
    // eslint-disable-next-line react/no-did-update-set-state
    this.setState(({ internalUsers }) => {
      const newUsers = usersChanged ? this.initUsers() : internalUsers;

      const userId = getUserIdFromValue(value);
      const selectedUser = userId ? newUsers.find(x => x.key === userId) : null;
      if (!selectedUser && userId) {
        onChange(null);
      }
      return {
        internalUsers: newUsers,
        selectedUser,
      };
    });
  }

  render() {
    const { placeholder, intl, disabled, emptyLabel, hideClearButton, small } = this.props;
    const { selectedUser, internalUsers } = this.state;

    return (
      <AutoCompleteWithoutInjectIntl
        items={internalUsers}
        placeholder={placeholder || intl.formatMessage(messages.SearchEmployee)}
        onChange={item => this.onUserChanged(item)}
        disabled={disabled}
        emptyLabel={emptyLabel}
        mode="edit"
        value={selectedUser}
        canClear={!hideClearButton}
        itemClassNameFunc={getUserClassName}
        isSmall={small}
        intl={intl}
      />
    );
  }

  static propTypes = {
    placeholder: PropTypes.string,
    intl: intlShape.isRequired,
    users: PropTypes.arrayOf(userShape),
    onChange: PropTypes.func.isRequired, // Returns full user object, not just id
    disabled: PropTypes.bool,
    emptyLabel: PropTypes.string,
    value: PropTypes.oneOfType([
      userShape,
      PropTypes.shape({ id: PropTypes.string.isRequired }),
      PropTypes.string,
    ]),
    withUnassigned: PropTypes.bool,
    withCurrentUser: PropTypes.bool,
    withSystemUser: PropTypes.bool,
    unassignedId: PropTypes.string,
    hideClearButton: PropTypes.bool,
    small: PropTypes.bool,
  };

  static defaultProps = {
    placeholder: null,
    disabled: false,
    emptyLabel: null,
    value: null,
    users: [],
    withUnassigned: false,
    withCurrentUser: false,
    withSystemUser: false,
    unassignedId: '',
    hideClearButton: false,
    small: false,
  };
}

export default UserSearchWithoutInjectIntl;
